import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// 表示と送信対象の項目
const PRIORITY_OPTIONS = [
  { id: "project_content", label: "プロジェクト内容" },
  { id: "remote_frequency", label: "リモート頻度" },
  { id: "amount", label: "金額" },
];

const SortableItem = ({ item, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.recordId || item.id,
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.5,
        mb: 1,
        bgcolor: "#fefefe",
        borderRadius: 1,
        border: "1px solid #dcdcdc",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        cursor: "grab",
        transition: "background 0.2s",
        "&:hover": {
          bgcolor: "#f9fafb",
        },
        ...style,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          width: 40,
          textAlign: "center",
          userSelect: "none", // テキスト選択を禁止
          WebkitUserSelect: "none", // Safari/iOS対応
          MozUserSelect: "none", // Firefox対策
          msUserSelect: "none", // IE対策
          touchAction: "none",
          WebkitTouchCallout: "none",
        }}
      >
        {index + 1}位
      </Typography>
      <Typography
        variant="body2"
        sx={{
          flexGrow: 1,
          whiteSpace: "nowrap",
          userSelect: "none",
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          touchAction: "none",
          WebkitTouchCallout: "none",
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
};

const SortablePriorityList = ({ defaultOrder }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (Array.isArray(defaultOrder) && defaultOrder.length > 0) {
      const ordered = defaultOrder
        .map(({ item_type, recordId }) => {
          const found = PRIORITY_OPTIONS.find((opt) => opt.id === item_type);
          return found ? { ...found, recordId } : null;
        })
        .filter(Boolean);
      setItems(ordered);
    } else {
      setItems([...PRIORITY_OPTIONS]);
    }
  }, [defaultOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 10, tolerance: 5 },
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex(
        (i) => (i.recordId || i.id) === active.id
      );
      const newIndex = items.findIndex((i) => (i.recordId || i.id) === over.id);
      setItems(arrayMove(items, oldIndex, newIndex));
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 14 }}>
          優先順位（ドラッグで変更）
        </Typography>
      </Box>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.recordId || item.id)}
          strategy={verticalListSortingStrategy}
        >
          <Box>
            {items.map((item, index) => {
              return (
                <React.Fragment key={item.recordId || item.id}>
                  <SortableItem item={item} index={index} />
                  <input
                    type="hidden"
                    name={`user_contract_request[user_contract_request_priorities_attributes][${index}][item_type]`}
                    value={item.id}
                  />
                  <input
                    type="hidden"
                    name={`user_contract_request[user_contract_request_priorities_attributes][${index}][priority]`}
                    value={index + 1}
                  />
                  {false && item.recordId && (
                    <input
                      type="hidden"
                      name={`user_contract_request[user_contract_request_priorities_attributes][${index}][id]`}
                      value={item.recordId}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default SortablePriorityList;
